import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import moment from "moment";
import {Box, Button, ButtonBase, CircularProgress, InputBase, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  CodeIcon, FloppyDiskIcon, InformationCircleIcon, Mail01Icon, MailSend01Icon, PlugSocketIcon, RotateLeft01Icon,
  Settings01Icon
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../../context/SignInContext";
import {useRegistryProject} from "../RegistryProjectContext";
import {Panel} from "../dashboard/components/Panel";
import {MailConnectorRepository} from "../../../repositories/MailConnectorRepository";
import {MailConnectorDialog} from "./MailConnectorDialog";

// A project has one template, saved under this name. Loading it returns the built-in template until the project
// saves its own.
const PROJECT_TEMPLATE_NAME = "default";

const DEFAULT_SUBJECT = "Test mail";
const DEFAULT_MESSAGE = "Hello,\n\nThank you for contacting us. We will get back to you shortly.\n\nKind regards";

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.response?.data?.error || fallback;

const escapeHtml = (text) => String(text ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

// {{content}} is where the backend puts the message; {{message}} is not a placeholder and renders empty
const CONTENT_PLACEHOLDER = /\{\{\s*content\s*\}\}/;
const MESSAGE_PLACEHOLDER = /\{\{\s*message\s*\}\}/;

// Sender name the backend uses for {{brand}} when the connector has none, or the project has no connector
const DEFAULT_BRAND = "OpenBrain";

// Preview only: the mail is rendered by the backend. Fills the template the way it does: {{content}} is the message
// (escaped, line breaks as <br/>), {{subject}}, {{brand}} (the sender name) and {{year}}; any other placeholder
// has no value (no variables are sent) and becomes empty.
export const renderMail = (template, {content, subject, brand}) => {
  const values = {
    content: escapeHtml(content).replace(/\r?\n/g, "<br/>"),
    subject: escapeHtml(subject.replace(/[\r\n]+/g, " ")),
    brand: escapeHtml(brand),
    year: String(new Date().getFullYear()),
  };
  return template.replace(/\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/g, (match, key) => values[key] ?? "");
};

const senderOf = (status) => status
  ? status.fromName ? `${status.fromName} <${status.fromAddress}>` : status.fromAddress || ""
  : "";

const fieldSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  px: 1.5,
  borderRadius: "12px",
  border: "1px solid #E4E4EE",
  bgcolor: "#fff",
  color: "#8A8AA0",
  "&:hover": {borderColor: "#D6D6E4"},
  "&:focus-within": {borderColor: "#8491F7", boxShadow: "0 0 0 3px rgba(132, 145, 247, 0.16)"},
};

const readOnlyFieldSx = {...fieldSx, height: 42, bgcolor: "#F4F4FA", "&:hover": {}};

const buttonSx = (dark) => ({
  height: 42, px: 2, borderRadius: "12px", textTransform: "none", fontSize: 14, fontWeight: 600,
  ...(dark
    ? {color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"}, "&.Mui-disabled": {color: "#fff", bgcolor: "#C9CAD8"}}
    : {color: "#23264D", border: "1px solid #E4E4EE", bgcolor: "#fff", "&:hover": {bgcolor: "#F4F4FA"}}),
});

const Label = ({children, actions}) => (
  <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{mb: 0.75, minHeight: 24}}>
    <Typography sx={{fontSize: 12, fontWeight: 500, color: "#6B6B80"}}>{children}</Typography>
    {actions}
  </Stack>
);

// Disabled address field (From / To)
const AddressField = ({label, value, loading}) => (
  <Box>
    <Label>{label}</Label>
    <Box sx={readOnlyFieldSx}>
      {loading ? <CircularProgress size={14} sx={{color: "#8491F7"}}/> : <HugeiconsIcon icon={Mail01Icon} size={16}/>}
      <InputBase
        value={loading ? "" : value}
        placeholder={loading ? "Loading…" : "—"}
        disabled
        readOnly
        fullWidth
        inputProps={{"aria-label": label}}
        sx={{fontSize: 14, "& .Mui-disabled": {WebkitTextFillColor: "#4A4A63"}}}
      />
    </Box>
  </Box>
);

// Connected (through the project's mailbox) or the default account, and the button that connects / edits the mailbox
const ConnectionStatus = ({status, loading, onOpen}) => {
  const connected = Boolean(status?.connected);
  return (
    <Stack direction={"row"} alignItems={"center"} gap={1.25} flexWrap={"wrap"}>
      {!loading && status && (
        <Box sx={{
          display: "inline-flex", alignItems: "center", gap: 0.75, height: 32, px: 1.5, borderRadius: "999px",
          fontSize: 13, fontWeight: 600,
          color: connected ? "#1E8A55" : "#A86A0C",
          bgcolor: connected ? "#E8F7EF" : "#FDF4E3",
        }}>
          <Box component={"span"} sx={{width: 7, height: 7, borderRadius: "50%", bgcolor: connected ? "#22C55E" : "#E0A232"}}/>
          {connected ? `Connected · ${status.connectorName}` : "Not connected"}
        </Box>
      )}
      <Button
        onClick={onOpen}
        disabled={loading}
        startIcon={<HugeiconsIcon icon={connected ? Settings01Icon : PlugSocketIcon} size={16}/>}
        sx={buttonSx(false)}
      >
        {connected ? "Mailbox settings" : "Connect mail"}
      </Button>
    </Stack>
  );
};

// Shown in the form while the project has no enabled mailbox
const NotConnectedNote = ({status, onConnect}) => (
  <Stack direction={"row"} gap={1} sx={{p: 1.5, borderRadius: "12px", bgcolor: "#FDF8EE", border: "1px solid #F5E6C8"}}>
    <Box sx={{color: "#A86A0C", display: "flex", pt: 0.25}}><HugeiconsIcon icon={InformationCircleIcon} size={16}/></Box>
    <Typography sx={{fontSize: 13, color: "#7A5A1E"}}>
      Mails go out from the default account {senderOf(status)}.{" "}
      <ButtonBase onClick={onConnect} sx={{fontSize: 13, fontWeight: 600, color: "#4B55C9", verticalAlign: "baseline"}}>
        Connect your mailbox
      </ButtonBase>{" "}
      to send from your own address.
    </Typography>
  </Stack>
);

const initialOf = (text) => (text?.trim()?.[0] || "?").toUpperCase();

// Right side: the mail as the recipient sees it in their inbox
const MailPreview = ({fromName, fromAddress, to, subject, html}) => (
  <Panel sx={{overflow: "hidden", minHeight: 560}}>
    <Stack direction={"row"} alignItems={"center"} gap={1} sx={{px: 3, py: 2, borderBottom: "1px solid #F0F0F6"}}>
      <HugeiconsIcon icon={MailSend01Icon} size={18} color={"#8491F7"}/>
      <Typography component={"h2"} sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
        Preview
      </Typography>
    </Stack>

    <Box sx={{px: 3, pt: 2, pb: 2, borderBottom: "1px solid #F2F2F7"}}>
      <Typography sx={{fontSize: 18, fontWeight: 600, color: "#23264D", mb: 1.5, wordBreak: "break-word"}}>
        {subject || <Box component={"span"} sx={{color: "#B4B4C3"}}>(no subject)</Box>}
      </Typography>
      <Stack direction={"row"} alignItems={"center"} gap={1.5}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
          justifyContent: "center", bgcolor: "#23264D", color: "#fff", fontSize: 15, fontWeight: 700,
        }}>
          {initialOf(fromName || fromAddress)}
        </Box>
        <Box sx={{minWidth: 0, flex: 1}}>
          <Stack direction={"row"} alignItems={"baseline"} justifyContent={"space-between"} gap={1}>
            <Typography noWrap sx={{fontSize: 14, color: "#23264D"}}>
              <b>{fromName || fromAddress || "—"}</b>
              {fromName && fromAddress && <Box component={"span"} sx={{color: "#8A8AA0", fontSize: 12}}> &lt;{fromAddress}&gt;</Box>}
            </Typography>
            <Typography sx={{fontSize: 12, color: "#8A8AA0", flexShrink: 0}}>{moment().format("DD MMM, HH:mm")}</Typography>
          </Stack>
          <Typography noWrap sx={{fontSize: 12, color: "#6B6B80"}}>to {to || "—"}</Typography>
        </Box>
      </Stack>
    </Box>

    {/* sandboxed: the template can't run scripts or reach the page */}
    <Box
      component={"iframe"}
      title={"Mail preview"}
      sandbox={""}
      srcDoc={`<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0}</style></head><body>${html}</body></html>`}
      sx={{flex: 1, width: "100%", minHeight: 460, border: 0, bgcolor: "#fff"}}
    />
  </Panel>
);

export const RegistryMailConnectorPage = () => {
  const {project} = useRegistryProject();
  const {currentUser} = useContext(SignInContext);
  const projectId = project?.id;
  const to = currentUser?.email || "";

  // sender
  const [status, setStatus] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [statusLoading, setStatusLoading] = useState(true);
  const [connectorDialogOpen, setConnectorDialogOpen] = useState(false);

  // the project's template: savedHtml as stored, template as edited
  const [savedHtml, setSavedHtml] = useState("");
  const [template, setTemplate] = useState("");
  const [templateLoading, setTemplateLoading] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);

  const dirty = template !== savedHtml;

  const loadConnection = useCallback(() => {
    if (!projectId) return;
    setStatusLoading(true);
    Promise.all([MailConnectorRepository.getStatus(projectId), MailConnectorRepository.getByProject(projectId)])
      .then(([statusResponse, connectorsResponse]) => {
        setStatus(statusResponse.data);
        setConnectors(connectorsResponse.data || []);
      })
      .catch(error => toast.error(errorMessage(error, "Failed to load the mail connection")))
      .finally(() => setStatusLoading(false));
  }, [projectId]);

  useEffect(loadConnection, [loadConnection]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setTemplateLoading(true);
    MailConnectorRepository.getTemplate(projectId, PROJECT_TEMPLATE_NAME)
      .then(({data}) => {
        if (cancelled) return;
        setSavedHtml(data?.html || "");
        setTemplate(data?.html || "");
      })
      .catch(error => !cancelled && toast.error(errorMessage(error, "Failed to load the mail template")))
      .finally(() => !cancelled && setTemplateLoading(false));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const saveTemplate = () => {
    setSavingTemplate(true);
    MailConnectorRepository.saveTemplate(projectId, PROJECT_TEMPLATE_NAME, template)
      .then(({data}) => {
        toast.success("Template saved");
        const html = data?.html ?? template;
        setSavedHtml(html);
        setTemplate(html);
      })
      .catch(error => toast.error(errorMessage(error, "Failed to save the template")))
      .finally(() => setSavingTemplate(false));
  };

  const brand = status?.fromName || DEFAULT_BRAND;
  const html = useMemo(() => renderMail(template, {content: message, subject, brand}), [template, message, subject, brand]);

  // The backend fills the template with the text. The saved template is sent by name; with unsaved changes the
  // edited template goes as html, so the mail matches the preview.
  const sendTest = () => {
    setSending(true);
    const body = dirty ? {html: template} : {templateName: PROJECT_TEMPLATE_NAME};
    MailConnectorRepository.send(projectId, {to: [to], subject: subject.trim(), text: message, ...body})
      .then(({data}) => toast.success(`Test mail sent to ${to}${data?.fromAddress ? ` from ${data.fromAddress}` : ""}`))
      .catch(error => toast.error(errorMessage(error, error?.response?.status === 502
        ? "The mail server rejected the mail, check the mailbox settings"
        : "Failed to send the test mail")))
      .finally(() => setSending(false));
  };

  // the connected mailbox, else one that is disabled, else a new one
  const editConnectorId = status?.connectorId || connectors[0]?.id || null;
  const closeConnectorDialog = useCallback(() => setConnectorDialogOpen(false), []);

  return (
    <Box sx={{flex: 1, minHeight: 0, bgcolor: "#F7F7FB", overflowY: "auto"}}>
      <Stack gap={3} sx={{maxWidth: 1280, mx: "auto", width: "100%", p: {xs: 2, md: 4}, pb: "100px", boxSizing: "border-box"}}>
        <Stack direction={{xs: "column", md: "row"}} justifyContent={"space-between"} alignItems={{md: "flex-end"}} gap={2}>
          <Box>
            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>CONNECTORS</Typography>
            <Typography component={"h1"} sx={{fontSize: 26, fontWeight: 700, color: "#23264D", letterSpacing: "-0.02em"}}>
              Mail Connector
            </Typography>
            <Typography sx={{fontSize: 14, color: "#6B6B80"}}>
              Send mails from your own mailbox and see how they look before they go out
            </Typography>
          </Box>
          <ConnectionStatus status={status} loading={statusLoading} onOpen={() => setConnectorDialogOpen(true)}/>
        </Stack>

        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)"}, gap: 2, alignItems: "start"}}>
          <Panel sx={{p: 3, gap: 2.5}}>
            <Box>
              <Typography component={"h2"} sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
                Compose
              </Typography>
              <Typography sx={{mt: 0.25, fontSize: 13, color: "#8A8AA0"}}>
                Write the message and the template it is sent in
              </Typography>
            </Box>

            {!statusLoading && status && !status.connected && (
              <NotConnectedNote status={status} onConnect={() => setConnectorDialogOpen(true)}/>
            )}

            <AddressField label={"From"} value={senderOf(status)} loading={statusLoading}/>
            <AddressField label={"To"} value={to}/>

            <Box>
              <Label>Subject</Label>
              <Box sx={{...fieldSx, height: 42}}>
                <InputBase
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={"Subject"}
                  fullWidth
                  inputProps={{"aria-label": "Subject"}}
                  sx={{fontSize: 14, color: "#23264D"}}
                />
              </Box>
            </Box>

            <Box>
              <Label>Message</Label>
              <Box sx={{...fieldSx, alignItems: "flex-start", py: 1.25}}>
                <InputBase
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={"Write your message…"}
                  multiline
                  minRows={6}
                  maxRows={14}
                  fullWidth
                  inputProps={{"aria-label": "Message"}}
                  sx={{fontSize: 14, color: "#23264D", lineHeight: 1.6}}
                />
              </Box>
            </Box>

            <Box>
              <Label actions={dirty && !templateLoading && (
                <ButtonBase onClick={() => setTemplate(savedHtml)} sx={{
                  gap: 0.5, px: 0.75, height: 24, borderRadius: "8px", fontSize: 12, fontWeight: 600, color: "#4B55C9",
                  "&:hover": {bgcolor: "rgba(132, 145, 247, 0.12)"},
                }}>
                  <HugeiconsIcon icon={RotateLeft01Icon} size={13}/>
                  Undo changes
                </ButtonBase>
              )}>
                HTML template
              </Label>
              <Box sx={{...fieldSx, alignItems: "flex-start", py: 1.25, position: "relative"}}>
                  <Box sx={{pt: 0.25, display: "flex"}}><HugeiconsIcon icon={CodeIcon} size={16}/></Box>
                  <InputBase
                    value={template}
                    onChange={(e) => setTemplate(e.target.value)}
                    disabled={templateLoading}
                    multiline
                    minRows={10}
                    maxRows={24}
                    fullWidth
                    inputProps={{"aria-label": "HTML template", spellCheck: false}}
                    sx={{fontSize: 12.5, color: "#23264D", fontFamily: "'JetBrains Mono', Consolas, monospace", lineHeight: 1.55}}
                  />
                  {templateLoading && (
                    <CircularProgress size={18} thickness={5} sx={{color: "#8491F7", position: "absolute", top: 12, right: 12}}/>
                  )}
              </Box>
              <Typography sx={{mt: 0.75, fontSize: 12, color: "#8A8AA0"}}>
                Placeholders:{" "}
                {["{{content}}", "{{subject}}", "{{brand}}", "{{year}}"].map(placeholder => (
                  <Box key={placeholder} component={"code"} sx={{px: 0.5, mr: 0.5, borderRadius: "4px", bgcolor: "#F2F2F7", color: "#4B55C9"}}>
                    {placeholder}
                  </Box>
                ))}
              </Typography>
              {!templateLoading && template.trim() && !CONTENT_PLACEHOLDER.test(template) && (
                <Stack direction={"row"} gap={1} sx={{mt: 1, p: 1.25, borderRadius: "12px", bgcolor: "#FDF4E3"}}>
                  <Box sx={{color: "#A86A0C", display: "flex", pt: 0.25}}><HugeiconsIcon icon={InformationCircleIcon} size={15}/></Box>
                  <Typography sx={{fontSize: 12, color: "#A86A0C"}}>
                    {MESSAGE_PLACEHOLDER.test(template)
                      ? <>{"{{message}}"} is not a placeholder and is sent empty. Use {"{{content}}"} for the message.</>
                      : <>The template has no {"{{content}}"}, so the message will not appear in the mail.</>}
                  </Typography>
                </Stack>
              )}
            </Box>

            <Stack direction={{xs: "column", sm: "row"}} justifyContent={"flex-end"} gap={1} sx={{pt: 0.5}}>
              <Button
                onClick={saveTemplate}
                disabled={!dirty || templateLoading || savingTemplate}
                startIcon={savingTemplate
                  ? <CircularProgress size={14} sx={{color: "#8491F7"}}/>
                  : <HugeiconsIcon icon={FloppyDiskIcon} size={16}/>}
                sx={buttonSx(false)}
              >
                {savingTemplate ? "Saving…" : "Save template"}
              </Button>
              <Button
                onClick={sendTest}
                disabled={sending || templateLoading || !to || !subject.trim()}
                startIcon={sending
                  ? <CircularProgress size={14} sx={{color: "#fff"}}/>
                  : <HugeiconsIcon icon={MailSend01Icon} size={16}/>}
                sx={buttonSx(true)}
              >
                {sending ? "Sending…" : "Send test mail"}
              </Button>
            </Stack>
          </Panel>

          <Box sx={{position: {lg: "sticky"}, top: {lg: 0}}}>
            <MailPreview fromName={status?.fromName} fromAddress={status?.fromAddress} to={to} subject={subject} html={html}/>
          </Box>
        </Box>
      </Stack>

      <MailConnectorDialog
        open={connectorDialogOpen}
        projectId={projectId}
        connectorId={editConnectorId}
        onClose={closeConnectorDialog}
        onSaved={loadConnection}
      />
    </Box>
  );
};
